import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { MenuItem, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { FormMultiSelect } from '../../fields/FormMultiSelect';
import { FormRadioGroup } from '../../fields/FormRadioGroup';
import { FormSelectField } from '../../fields/FormSelectField';
import { FormTextField } from '../../fields/FormTextField';
import { useFormContext } from '../../FormContext';
var AssignActionTab;
(function (AssignActionTab) {
    AssignActionTab["Value"] = "Value";
    AssignActionTab["Computed"] = "Computed";
})(AssignActionTab || (AssignActionTab = {}));
const StyledConfiguratorDiv = styled.div `
  .field {
    margin-bottom: 12px;
  }
  .tab-panel {
    padding: 12px;
  }
  .compute-label {
    margin-bottom: 12px;
  }
  code {
    background: #f4f5fa;
  }
  .table {
    margin-top: 12px;
  }
`;
const ActionConfigurator = ({ action, onChange }) => {
    var _a, _b;
    const { entitySchema, formState, trackChanges } = useFormContext();
    const { selectedStateNode } = formState;
    const [params, setParams] = useState(action.params || {});
    const [tab, setTab] = useState(params.isComputed ? AssignActionTab.Computed : AssignActionTab.Value);
    const column = useMemo(() => {
        var _a;
        return (_a = entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns) === null || _a === void 0 ? void 0 : _a.find(c => { var _a; return c.name === ((_a = action.params) === null || _a === void 0 ? void 0 : _a.schemaColumnName); });
    }, [(_a = action.params) === null || _a === void 0 ? void 0 : _a.schemaColumnName, entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns]);
    const formElement = useMemo(() => {
        var _a, _b, _c, _d;
        let formElement = undefined;
        if (params.schemaColumnName) {
            // find form element in current section
            formElement = (_b = (_a = selectedStateNode === null || selectedStateNode === void 0 ? void 0 : selectedStateNode.meta) === null || _a === void 0 ? void 0 : _a.elements) === null || _b === void 0 ? void 0 : _b.find(element => element.dataSource === params.schemaColumnName);
            // if not found find element in all sections
            if (!formElement && trackChanges.final.states) {
                for (const stateNode of trackChanges.final.states) {
                    formElement = (_d = (_c = stateNode === null || stateNode === void 0 ? void 0 : stateNode.meta) === null || _c === void 0 ? void 0 : _c.elements) === null || _d === void 0 ? void 0 : _d.find(element => element.dataSource === params.schemaColumnName);
                    if (formElement) {
                        break;
                    }
                }
            }
            return formElement;
        }
    }, [
        params.schemaColumnName,
        (_b = selectedStateNode === null || selectedStateNode === void 0 ? void 0 : selectedStateNode.meta) === null || _b === void 0 ? void 0 : _b.elements,
        trackChanges.final.states
    ]);
    const columnType = ((column === null || column === void 0 ? void 0 : column.type) || '').replace(/\([\d,]+\)/g, '');
    return (React.createElement(StyledConfiguratorDiv, null,
        React.createElement(Autocomplete, { fullWidth: true, disableClearable: true, className: 'field', getOptionLabel: column => (column === null || column === void 0 ? void 0 : column.displayName) || '', getOptionSelected: (column, value) => (column === null || column === void 0 ? void 0 : column.name) === (value === null || value === void 0 ? void 0 : value.name), value: (entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns.find(column => { var _a; return column.name === ((_a = action.params) === null || _a === void 0 ? void 0 : _a.schemaColumnName); })) || null, options: (entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns) || [], onChange: (_, value) => {
                const schemaColumnName = value.name;
                params.schemaColumnName = schemaColumnName;
                action.params = params;
                setParams(Object.assign({}, params));
                onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
            }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', variant: 'outlined', label: 'Schema Column' }))) }),
        ((formElement === null || formElement === void 0 ? void 0 : formElement.type) || column) && (React.createElement(React.Fragment, null,
            React.createElement(Tabs, { className: 'tabs', indicatorColor: 'primary', value: tab, onChange: (_, value) => {
                    setTab(value);
                    params.isComputed = value === AssignActionTab.Computed;
                    setParams(Object.assign({}, params));
                    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                } },
                React.createElement(Tab, { label: 'Assign Value', value: AssignActionTab.Value }),
                React.createElement(Tab, { label: 'Compute Value', value: AssignActionTab.Computed })),
            React.createElement("div", { className: 'tab-panel' }, (() => {
                if (tab === AssignActionTab.Computed) {
                    return (React.createElement(React.Fragment, null,
                        React.createElement(Typography, { gutterBottom: true, variant: 'body2', className: 'compute-label' },
                            "Compute a value by using javascript syntax (i.e.",
                            ' ',
                            (() => {
                                const variable = params.schemaColumnName || 'variableName';
                                switch (columnType) {
                                    case 'integer':
                                    case 'number':
                                        return `${variable} * 2`;
                                    default:
                                        if (column === null || column === void 0 ? void 0 : column.array) {
                                            return (React.createElement("code", null,
                                                "$newValue.includes('None') ? ['None'] :",
                                                ' ',
                                                variable,
                                                ".filter(s => s !== 'None')"));
                                        }
                                        else {
                                            return `${variable} = 'Text'`;
                                        }
                                }
                            })(),
                            ")"),
                        React.createElement(TextField, { multiline: true, fullWidth: true, variant: 'outlined', size: 'small', label: 'Compute a value', value: params.value || '', onChange: event => {
                                params.value = event.target.value;
                                action.params = params;
                                setParams(Object.assign({}, params));
                                onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                            } }),
                        React.createElement(Table, { size: 'small', className: 'table' },
                            React.createElement(TableHead, null,
                                React.createElement(TableRow, null,
                                    React.createElement(TableCell, null, "Variable"),
                                    React.createElement(TableCell, null, "Description"))),
                            React.createElement(TableBody, null,
                                React.createElement(TableRow, null,
                                    React.createElement(TableCell, null,
                                        React.createElement("code", null, "$newValue")),
                                    React.createElement(TableCell, null,
                                        "When an element value is changed",
                                        ' ',
                                        React.createElement("code", null, "$newValue"),
                                        " contains the changed value. Only applies for element actions.")),
                                React.createElement(TableRow, null,
                                    React.createElement(TableCell, null,
                                        React.createElement("code", null, "$previousValue")),
                                    React.createElement(TableCell, null, "The previous value prior to value change.")),
                                React.createElement(TableRow, null,
                                    React.createElement(TableCell, null,
                                        React.createElement("code", null, "$previousFormValues")),
                                    React.createElement(TableCell, null, "The previous form values prior to last form value change.")), entitySchema === null || entitySchema === void 0 ? void 0 :
                                entitySchema.columns.map(column => (React.createElement(TableRow, { key: column.name },
                                    React.createElement(TableCell, null,
                                        React.createElement("code", null, column.name)),
                                    React.createElement(TableCell, null,
                                        "Reference to the \"",
                                        column.displayName,
                                        "\" value in the form."))))))));
                }
                else if (formElement === null || formElement === void 0 ? void 0 : formElement.type) {
                    switch (formElement === null || formElement === void 0 ? void 0 : formElement.type) {
                        case 'Select':
                            return (React.createElement(FormSelectField, { stateNode: selectedStateNode, element: formElement, value: params.value || '', onChange: (event, value) => {
                                    event.preventDefault();
                                    params.value = value;
                                    action.params = params;
                                    setParams(Object.assign({}, params));
                                    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                                } }));
                        case 'RadioGroup':
                            return (React.createElement(FormRadioGroup, { stateNode: selectedStateNode, element: formElement, value: typeof params.value !== 'undefined'
                                    ? params.value
                                    : '', onChange: (event, value) => {
                                    event.preventDefault();
                                    params.value = value;
                                    action.params = params;
                                    setParams(Object.assign({}, params));
                                    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                                } }));
                        case 'MultiSelect':
                            return (React.createElement(FormMultiSelect, { stateNode: selectedStateNode, element: formElement, values: Array.isArray(params.value) ? params.value : [], onChange: (event, option) => {
                                    event.preventDefault();
                                    const values = Array.isArray(params.value)
                                        ? params.value
                                        : [];
                                    const index = values.indexOf(option.value);
                                    if (index > -1) {
                                        values.splice(index, 1);
                                    }
                                    else {
                                        values.push(option.value);
                                    }
                                    params.value = values;
                                    action.params = params;
                                    setParams(Object.assign({}, params));
                                    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                                } }));
                        case 'Slider':
                        case 'NumberField':
                        case 'TextField':
                            return (React.createElement(FormTextField, { stateNode: selectedStateNode, element: formElement, value: params.value || '', onChange: (event, value) => {
                                    event.preventDefault();
                                    params.value = value;
                                    action.params = params;
                                    setParams(Object.assign({}, params));
                                    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                                } }));
                        case 'Date':
                        case 'Time':
                        default:
                            return (React.createElement(Alert, { severity: 'warning' },
                                "The form element type ", formElement === null || formElement === void 0 ? void 0 :
                                formElement.type,
                                " is not yet implemented for assignment."));
                    }
                }
                else if (columnType) {
                    let type = 'text';
                    switch (columnType) {
                        case 'numeric':
                        case 'integer':
                            type = 'number';
                        // eslint-disable-next-line no-fallthrough
                        case 'text':
                            return (React.createElement(TextField, { fullWidth: true, type: type, size: 'small', variant: 'outlined', label: 'Assign Value', value: params.value || '', onChange: event => {
                                    params.value = event.target.value;
                                    action.params = params;
                                    setParams(Object.assign({}, params));
                                    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                                } }));
                        case 'boolean':
                            return (React.createElement(TextField, { select: true, fullWidth: true, size: 'small', variant: 'outlined', label: 'Assign Value', value: params.value || '', onChange: event => {
                                    params.value = event.target.value;
                                    console.log('typeof params.value', typeof params.value, params.value);
                                    action.params = params;
                                    setParams(Object.assign({}, params));
                                    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, action));
                                } },
                                React.createElement(MenuItem, { value: 1 }, "True"),
                                React.createElement(MenuItem, { value: 0 }, "False")));
                        case 'timestamp':
                        case 'date':
                        case 'time':
                        default:
                            return (React.createElement(Alert, { severity: 'warning' },
                                "The form column type ", column === null || column === void 0 ? void 0 :
                                column.type,
                                " is not yet implemented for assignment."));
                    }
                }
            })())))));
};
const StyledDisplayDiv = styled.div `
  font-size: 13px;
  padding-left: 4px;
  text-indent: -4px;
  code {
    background: #f4f5fa;
    padding: 2px 5px;
    border-radius: 3px;
  }
  b {
    padding: 0 5px 0 0;
  }
  i {
  }
`;
const AssignActionDisplay = ({ action }) => {
    const { params } = action;
    return (React.createElement(StyledDisplayDiv, null,
        React.createElement("code", null, params === null || params === void 0 ? void 0 : params.schemaColumnName),
        React.createElement("b", null, "="),
        React.createElement("i", null, Array.isArray(params === null || params === void 0 ? void 0 : params.value)
            ? params === null || params === void 0 ? void 0 : params.value.join(', ')
            : params === null || params === void 0 ? void 0 : params.value)));
};
export const AssignActionPlugin = {
    type: 'Assign',
    runOnUpdate: true,
    configurator: ActionConfigurator,
    display: AssignActionDisplay,
    validate: (condition) => {
        return true;
    },
    action: (state, params) => {
        const { setFormValue, previousFormValues, entitySchema } = state.context;
        let value = params.value;
        if (params.isComputed) {
            const formValues = state.context.formValues;
            const previousValue = previousFormValues[params.schemaColumnName];
            let newValue = formValues[params.schemaColumnName];
            if (Array.isArray(newValue)) {
                // get changed (delta) value
                newValue = newValue.filter((v) => !(previousValue === null || previousValue === void 0 ? void 0 : previousValue.includes(v)));
            }
            const keys = Object.keys(formValues);
            for (const column of (entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns) || []) {
                const index = keys.indexOf(column.name);
                if (index === -1) {
                    if (['integer', 'numeric'].includes(column.type)) {
                        keys.push(`${column.name} = 0`); // default to 0
                    }
                    else {
                        keys.push(column.name);
                    }
                }
            }
            // eslint-disable-next-line no-new-func
            value = new Function(`const { ${keys.join(', ')} } = this.formValues;
         const $previousFormValues = this.previousFormValues;
         const $previousValue = this.previousFormValue;
         const $newValue = this.newValue;
         try {
          return ${value};
         } catch (error) {
          console.error(error);
        }`).call({ formValues, previousFormValues, previousValue, newValue });
            // force context formValues update after computed change
            formValues[params.schemaColumnName] = value;
        }
        setFormValue(params.schemaColumnName, value, undefined, true);
    }
};
//# sourceMappingURL=AssignAction.js.map